// Stopwatch persistence utility functions
import { saveToLocalStorage, loadFromLocalStorage, removeFromLocalStorage } from '../../utils/localStorage';

const STORAGE_KEYS = {
    STOPWATCH_CONFIG: 'stopwatch_config',
    STOPWATCH_GRID: 'stopwatch_grid',
    STOPWATCH_ACTIVE: 'stopwatch_active',
    STOPWATCH_TIMES: 'stopwatch_recorded_times',
    STOPWATCH_CURRENT_TIMES: 'stopwatch_current_times',
    STOPWATCH_LAP_TIMES: 'stopwatch_lap_times',
    STOPWATCH_START_TIMES: 'stopwatch_start_times',
    STOPWATCH_LAP_START_TIMES: 'stopwatch_lap_start_times',
    STOPWATCH_UNKNOWN_TIMES: 'stopwatch_unknown_times',
    STOPWATCH_SETUP_MODE: 'stopwatch_setup_mode',
    STOPWATCH_LAST_SAVE: 'stopwatch_last_save',
    STOPWATCH_SAVED_SESSIONS: 'stopwatch_saved_sessions'
};

// Speichere kompletten Stopwatch-Zustand
export const saveStopwatchState = (state) => {
    const {
        lanes,
        rows,
        grid,
        activeStopwatches,
        recordedTimes,
        currentTimes,
        lapTimes,
        startTimes,
        lapStartTimes,
        unknownTimes,
        isSetupMode
    } = state;

    const timestamp = Date.now();

    // Speichere alle Teile des Zustands
    saveToLocalStorage(STORAGE_KEYS.STOPWATCH_CONFIG, { lanes, rows });
    saveToLocalStorage(STORAGE_KEYS.STOPWATCH_GRID, grid);
    saveToLocalStorage(STORAGE_KEYS.STOPWATCH_ACTIVE, activeStopwatches);
    saveToLocalStorage(STORAGE_KEYS.STOPWATCH_TIMES, recordedTimes);
    saveToLocalStorage(STORAGE_KEYS.STOPWATCH_CURRENT_TIMES, currentTimes);
    saveToLocalStorage(STORAGE_KEYS.STOPWATCH_LAP_TIMES, lapTimes);
    saveToLocalStorage(STORAGE_KEYS.STOPWATCH_START_TIMES, startTimes);
    saveToLocalStorage(STORAGE_KEYS.STOPWATCH_LAP_START_TIMES, lapStartTimes);
    saveToLocalStorage(STORAGE_KEYS.STOPWATCH_UNKNOWN_TIMES, unknownTimes);
    saveToLocalStorage(STORAGE_KEYS.STOPWATCH_SETUP_MODE, isSetupMode);
    saveToLocalStorage(STORAGE_KEYS.STOPWATCH_LAST_SAVE, timestamp);

    console.log('Stopwatch state saved successfully at', new Date(timestamp));
    return true;
};

// Lade kompletten Stopwatch-Zustand
export const loadStopwatchState = () => {
    try {
        const config = loadFromLocalStorage(STORAGE_KEYS.STOPWATCH_CONFIG);
        const grid = loadFromLocalStorage(STORAGE_KEYS.STOPWATCH_GRID);
        const activeStopwatches = loadFromLocalStorage(STORAGE_KEYS.STOPWATCH_ACTIVE);
        const recordedTimes = loadFromLocalStorage(STORAGE_KEYS.STOPWATCH_TIMES);
        const currentTimes = loadFromLocalStorage(STORAGE_KEYS.STOPWATCH_CURRENT_TIMES);
        const lapTimes = loadFromLocalStorage(STORAGE_KEYS.STOPWATCH_LAP_TIMES);
        const startTimes = loadFromLocalStorage(STORAGE_KEYS.STOPWATCH_START_TIMES);
        const lapStartTimes = loadFromLocalStorage(STORAGE_KEYS.STOPWATCH_LAP_START_TIMES);
        const unknownTimes = loadFromLocalStorage(STORAGE_KEYS.STOPWATCH_UNKNOWN_TIMES);
        const isSetupMode = loadFromLocalStorage(STORAGE_KEYS.STOPWATCH_SETUP_MODE);
        const lastSave = loadFromLocalStorage(STORAGE_KEYS.STOPWATCH_LAST_SAVE);

        // Prüfe ob alle erforderlichen Daten vorhanden sind
        if (!config || !grid) {
            return null;
        }

        // Berechne verstrichene Zeit für aktive Timer
        const now = Date.now();
        const adjustedCurrentTimes = { ...currentTimes };
        const adjustedLapTimes = { ...lapTimes };
        const adjustedStartTimes = { ...startTimes };
        const adjustedLapStartTimes = { ...lapStartTimes };

        if (activeStopwatches && startTimes && lastSave) {
            const timeDifference = now - lastSave;
            
            // Aktualisiere laufende Timer mit verstrichener Zeit
            Object.keys(activeStopwatches).forEach(key => {
                if (activeStopwatches[key] && startTimes[key]) {
                    // Hauptzeit: füge verstrichene Zeit hinzu
                    if (currentTimes[key] !== undefined) {
                        adjustedCurrentTimes[key] = currentTimes[key] + timeDifference;
                    }
                    
                    // Rundenzeit: füge verstrichene Zeit hinzu
                    if (lapTimes[key] !== undefined) {
                        adjustedLapTimes[key] = lapTimes[key] + timeDifference;
                    }
                    
                    // Passe Startzeiten an
                    adjustedStartTimes[key] = now - (currentTimes[key] + timeDifference);
                    adjustedLapStartTimes[key] = now - (lapTimes[key] + timeDifference);
                }
            });
        }

        console.log('Stopwatch state loaded successfully. Last saved:', new Date(lastSave));

        return {
            lanes: config.lanes || 4,
            rows: config.rows || 3,
            grid: grid || [],
            activeStopwatches: activeStopwatches || {},
            recordedTimes: recordedTimes || {},
            currentTimes: adjustedCurrentTimes || {},
            lapTimes: adjustedLapTimes || {},
            startTimes: adjustedStartTimes || {},
            lapStartTimes: adjustedLapStartTimes || {},
            unknownTimes: unknownTimes || [],
            isSetupMode: isSetupMode !== null ? isSetupMode : true
        };
    } catch (error) {
        console.error('Error loading stopwatch state:', error);
        return null;
    }
};

// Lösche gespeicherten Stopwatch-Zustand
export const clearStopwatchState = () => {
    Object.values(STORAGE_KEYS).forEach(key => {
        removeFromLocalStorage(key);
    });
    console.log('Stopwatch state cleared');
    return true;
};

// Prüfe ob gespeicherter Zustand vorhanden ist
export const hasStopwatchState = () => {
    const config = loadFromLocalStorage(STORAGE_KEYS.STOPWATCH_CONFIG);
    const grid = loadFromLocalStorage(STORAGE_KEYS.STOPWATCH_GRID);
    return config !== null && grid !== null;
};

// Speichere nur die Konfiguration (Bahnen/Reihen)
export const saveStopwatchConfig = (lanes, rows) => {
    return saveToLocalStorage(STORAGE_KEYS.STOPWATCH_CONFIG, { lanes, rows });
};

// Lade nur die Konfiguration
export const loadStopwatchConfig = () => {
    return loadFromLocalStorage(STORAGE_KEYS.STOPWATCH_CONFIG, { lanes: 4, rows: 3 });
};

// === BENANNTE SITZUNGEN ===

// Speichere eine benannte Sitzung
export const saveNamedSession = (name, state) => {
    try {
        const sessions = loadFromLocalStorage(STORAGE_KEYS.STOPWATCH_SAVED_SESSIONS, {});
        const sessionId = generateSessionId();
        const timestamp = Date.now();
        
        const sessionData = {
            id: sessionId,
            name: name.trim(),
            timestamp,
            lastModified: timestamp,
            state: {
                ...state,
                // Behalte Timer-Status beim Speichern bei
                lastSaveTimestamp: timestamp
            }
        };
        
        sessions[sessionId] = sessionData;
        saveToLocalStorage(STORAGE_KEYS.STOPWATCH_SAVED_SESSIONS, sessions);
        
        console.log(`Named session "${name}" saved with ID: ${sessionId}`);
        return sessionId;
    } catch (error) {
        console.error('Error saving named session:', error);
        return null;
    }
};

// Lade alle gespeicherten Sitzungen
export const loadSavedSessions = () => {
    try {
        const sessions = loadFromLocalStorage(STORAGE_KEYS.STOPWATCH_SAVED_SESSIONS, {});
        return Object.values(sessions).sort((a, b) => b.lastModified - a.lastModified);
    } catch (error) {
        console.error('Error loading saved sessions:', error);
        return [];
    }
};

// Lade eine spezifische Sitzung
export const loadNamedSession = (sessionId) => {
    try {
        const sessions = loadFromLocalStorage(STORAGE_KEYS.STOPWATCH_SAVED_SESSIONS, {});
        const session = sessions[sessionId];
        
        if (!session) {
            return null;
        }

        // Wenn die Sitzung Timer-States hat, berechne die verstrichene Zeit
        if (session.state && session.state.activeStopwatches && session.state.lastSaveTimestamp) {
            const now = Date.now();
            const timeDifference = now - session.state.lastSaveTimestamp;
            
            const adjustedCurrentTimes = { ...session.state.currentTimes };
            const adjustedLapTimes = { ...session.state.lapTimes };
            const adjustedStartTimes = { ...session.state.startTimes };
            const adjustedLapStartTimes = { ...session.state.lapStartTimes };

            // Aktualisiere laufende Timer mit verstrichener Zeit
            Object.keys(session.state.activeStopwatches).forEach(key => {
                if (session.state.activeStopwatches[key] && session.state.startTimes && session.state.startTimes[key]) {
                    // Hauptzeit: füge verstrichene Zeit hinzu
                    if (session.state.currentTimes && session.state.currentTimes[key] !== undefined) {
                        adjustedCurrentTimes[key] = session.state.currentTimes[key] + timeDifference;
                    }
                    
                    // Rundenzeit: füge verstrichene Zeit hinzu
                    if (session.state.lapTimes && session.state.lapTimes[key] !== undefined) {
                        adjustedLapTimes[key] = session.state.lapTimes[key] + timeDifference;
                    }
                    
                    // Passe Startzeiten an
                    adjustedStartTimes[key] = now - adjustedCurrentTimes[key];
                    if (session.state.lapStartTimes && session.state.lapStartTimes[key]) {
                        adjustedLapStartTimes[key] = now - adjustedLapTimes[key];
                    }
                }
            });

            // Gib die Sitzung mit angepassten Timer-States zurück
            return {
                ...session,
                state: {
                    ...session.state,
                    currentTimes: adjustedCurrentTimes,
                    lapTimes: adjustedLapTimes,
                    startTimes: adjustedStartTimes,
                    lapStartTimes: adjustedLapStartTimes
                }
            };
        }

        return session;
    } catch (error) {
        console.error('Error loading named session:', error);
        return null;
    }
};

// Lösche eine benannte Sitzung
export const deleteNamedSession = (sessionId) => {
    try {
        const sessions = loadFromLocalStorage(STORAGE_KEYS.STOPWATCH_SAVED_SESSIONS, {});
        if (sessions[sessionId]) {
            delete sessions[sessionId];
            saveToLocalStorage(STORAGE_KEYS.STOPWATCH_SAVED_SESSIONS, sessions);
            console.log(`Named session with ID ${sessionId} deleted`);
            return true;
        }
        return false;
    } catch (error) {
        console.error('Error deleting named session:', error);
        return false;
    }
};

// Aktualisiere den Namen einer Sitzung
export const updateSessionName = (sessionId, newName) => {
    try {
        const sessions = loadFromLocalStorage(STORAGE_KEYS.STOPWATCH_SAVED_SESSIONS, {});
        if (sessions[sessionId]) {
            sessions[sessionId].name = newName.trim();
            sessions[sessionId].lastModified = Date.now();
            saveToLocalStorage(STORAGE_KEYS.STOPWATCH_SAVED_SESSIONS, sessions);
            console.log(`Session name updated to "${newName}"`);
            return true;
        }
        return false;
    } catch (error) {
        console.error('Error updating session name:', error);
        return false;
    }
};

// Aktualisiere den Zustand einer existierenden Sitzung
export const updateNamedSession = (sessionId, state) => {
    try {
        const sessions = loadFromLocalStorage(STORAGE_KEYS.STOPWATCH_SAVED_SESSIONS, {});
        if (sessions[sessionId]) {
            sessions[sessionId].state = {
                ...state,
                // Behalte Timer-Status beim Speichern bei
                lastSaveTimestamp: Date.now()
            };
            sessions[sessionId].lastModified = Date.now();
            saveToLocalStorage(STORAGE_KEYS.STOPWATCH_SAVED_SESSIONS, sessions);
            console.log(`Session with ID ${sessionId} updated`);
            return true;
        }
        return false;
    } catch (error) {
        console.error('Error updating named session:', error);
        return false;
    }
};

// Hilfsfunktion zur Generierung einer eindeutigen Session-ID
const generateSessionId = () => {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Prüfe ob es gespeicherte Sitzungen gibt
export const hasSavedSessions = () => {
    const sessions = loadSavedSessions();
    return sessions.length > 0;
};
